import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ExternalLink, Building2, Wallet } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import LanguageSelector from '../ui/LanguageSelector';
import { useLanguage } from '../../contexts/LanguageContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/dtw57gqk_holdergroup.png';
const HAS_LOGO_URL = 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/ea0sayna_1.5_Aplica%C3%A7%C3%A3o-de-Logotipo_Fundo-Escuro.jpg';

const PORTAL_HOLDER_URL = 'https://portal.holdcorp.com.br/';
const HAS_BANKING_URL = 'https://login.contapag.com.br/auth/realms/7c28bd15-a434-4a79-9754-fo/protocol/openid-connect/auth?client_id=internet-payment-holdaccount&redirect_uri=https%3A%2F%2Fweb-holdaccount.pagme.app%2F&state=holder-bilingual&response_mode=fragment&response_type=code&scope=openid&nonce=holder-bilingual';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-header shadow-sm' : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Holder & Co. Group" 
              className="h-12 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-bronze ${
                isActive('/') ? 'text-bronze' : 'text-stone-700'
              }`}
              data-testid="nav-link-inicio"
            >
              {t('nav.home')}
            </Link>
            
            <Link
              to="/sobre"
              className={`text-sm font-medium transition-colors hover:text-bronze ${
                isActive('/sobre') ? 'text-bronze' : 'text-stone-700'
              }`}
              data-testid="nav-link-sobre"
            >
              {t('nav.about')}
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger 
                className="flex items-center gap-1 text-sm font-medium transition-colors hover:text-bronze text-stone-700"
                data-testid="nav-dropdown-servicos"
              >
                {t('nav.services')}
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/servicos" className="w-full">{t('nav.allServices')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/gestao-fiscal" className="w-full">{t('services.fiscalManagement')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/gestao-positiva" className="w-full">{t('services.positiveTax')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/gestao-passivos" className="w-full">{t('services.liabilities')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/ma" className="w-full">{t('services.ma')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/credito-operacoes" className="w-full">{t('services.credit')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/assessoria-juridica" className="w-full">{t('services.legal')}</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/parceiros"
              className={`text-sm font-medium transition-colors hover:text-bronze ${
                isActive('/parceiros') ? 'text-bronze' : 'text-stone-700'
              }`}
              data-testid="nav-link-parceiros"
            >
              {t('nav.partners')}
            </Link>

            <Link
              to="/contato"
              className={`text-sm font-medium transition-colors hover:text-bronze ${
                isActive('/contato') ? 'text-bronze' : 'text-stone-700'
              }`}
              data-testid="nav-link-contato"
            >
              {t('nav.contact')}
            </Link>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSelector />
            
            <Dialog open={accessDialogOpen} onOpenChange={setAccessDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="border-emerald text-emerald hover:bg-emerald hover:text-white rounded-sm px-6"
                  data-testid="header-access-btn"
                >
                  {t('nav.access')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-center text-xl font-serif">{t('access.title')}</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <a
                    href={PORTAL_HOLDER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 border border-stone-200 rounded-sm hover:bg-stone-50 hover:border-emerald transition-all group"
                    data-testid="access-portal-holder"
                    onClick={() => setAccessDialogOpen(false)}
                  >
                    <div className="w-12 h-12 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald group-hover:bg-emerald group-hover:text-white transition-colors">
                      <Building2 className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-serif font-semibold text-stone-900">{t('access.portalHolder')}</p>
                      <p className="text-sm text-stone-500">{t('access.portalHolderDesc')}</p>
                    </div>
                    <ExternalLink className="h-5 w-5 text-stone-400 group-hover:text-emerald" />
                  </a>

                  <a
                    href={HAS_BANKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 border border-stone-200 rounded-sm hover:bg-stone-50 hover:border-bronze transition-all group"
                    data-testid="access-has-banking"
                    onClick={() => setAccessDialogOpen(false)}
                  >
                    <div className="w-12 h-12 rounded overflow-hidden">
                      <img src={HAS_LOGO_URL} alt="HAS" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <p className="font-serif font-semibold text-stone-900">{t('access.hasBanking')}</p>
                      <p className="text-sm text-stone-500">{t('access.hasBankingDesc')}</p>
                    </div>
                    <ExternalLink className="h-5 w-5 text-stone-400 group-hover:text-bronze" />
                  </a>
                </div>
              </DialogContent>
            </Dialog>

            <Button 
              asChild
              className="bg-emerald text-white hover:bg-emerald-light rounded-sm px-6"
              data-testid="header-cta-btn"
            >
              <Link to="/contato">{t('nav.contactUs')}</Link>
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-3">
            <LanguageSelector />
            <button
              className="p-2"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="h-6 w-6 text-stone-700" />
              ) : (
                <Menu className="h-6 w-6 text-stone-700" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div 
          className="lg:hidden bg-white border-t border-stone-200 shadow-lg"
          data-testid="mobile-nav"
        >
          <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-2">
            <Link to="/" className="text-sm font-medium py-2 text-stone-700 hover:text-bronze">{t('nav.home')}</Link>
            <Link to="/sobre" className="text-sm font-medium py-2 text-stone-700 hover:text-bronze">{t('nav.about')}</Link>
            <span className="text-sm font-semibold text-stone-700 py-2">{t('nav.services')}</span>
            <Link to="/servicos" className="text-sm py-2 pl-4 text-stone-600 hover:text-bronze">{t('nav.allServices')}</Link>
            <Link to="/gestao-fiscal" className="text-sm py-2 pl-4 text-stone-600 hover:text-bronze">{t('services.fiscalManagement')}</Link>
            <Link to="/gestao-positiva" className="text-sm py-2 pl-4 text-stone-600 hover:text-bronze">{t('services.positiveTax')}</Link>
            <Link to="/gestao-passivos" className="text-sm py-2 pl-4 text-stone-600 hover:text-bronze">{t('services.liabilities')}</Link>
            <Link to="/ma" className="text-sm py-2 pl-4 text-stone-600 hover:text-bronze">{t('services.ma')}</Link>
            <Link to="/credito-operacoes" className="text-sm py-2 pl-4 text-stone-600 hover:text-bronze">{t('services.credit')}</Link>
            <Link to="/assessoria-juridica" className="text-sm py-2 pl-4 text-stone-600 hover:text-bronze">{t('services.legal')}</Link>
            <Link to="/parceiros" className="text-sm font-medium py-2 text-stone-700 hover:text-bronze">{t('nav.partners')}</Link>
            <Link to="/contato" className="text-sm font-medium py-2 text-stone-700 hover:text-bronze">{t('nav.contact')}</Link>
            
            <div className="mt-4 pt-4 border-t border-stone-200">
              <p className="text-sm font-semibold text-stone-700 mb-2">{t('nav.access')}</p>
              <a 
                href={PORTAL_HOLDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm py-2 text-emerald hover:text-emerald-light"
              >
                <Building2 className="h-4 w-4" />
                {t('access.portalHolder')}
                <ExternalLink className="h-3 w-3" />
              </a>
              <a 
                href={HAS_BANKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm py-2 text-bronze hover:text-bronze-light"
              >
                <Wallet className="h-4 w-4" />
                {t('access.hasBanking')}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            <Button 
              asChild
              className="mt-4 bg-emerald text-white hover:bg-emerald-light rounded-sm w-full"
              data-testid="mobile-cta-btn"
            >
              <Link to="/contato">{t('nav.contactUs')}</Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
